import { useState } from "react";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import { Button } from "../../../../components/common/button";
import { Text } from "../../../../components/common/typography";
import { DatePicker } from "../../../../components/common/date-picker";

const PRESETS = ["Today", "This Week", "This Month", "This Quarter", "This Year"] as const;
type Preset = (typeof PRESETS)[number];

function toDateStr(d: Date) {
  return d.toISOString().split("T")[0];
}

export function computePresetDates(preset: Preset): { start: string; end: string } {
  const now = new Date();
  const today = toDateStr(now);
  switch (preset) {
    case "Today":
      return { start: today, end: today };
    case "This Week": {
      const day = now.getDay();
      const mon = new Date(now);
      mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
      return { start: toDateStr(mon), end: today };
    }
    case "This Month": {
      return { start: toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)), end: today };
    }
    case "This Quarter": {
      const q = Math.floor(now.getMonth() / 3);
      return { start: toDateStr(new Date(now.getFullYear(), q * 3, 1)), end: today };
    }
    case "This Year":
      return { start: toDateStr(new Date(now.getFullYear(), 0, 1)), end: today };
  }
}

interface Props {
  startDate: string;
  endDate: string;
  onStartDateChange: (v: string) => void;
  onEndDateChange: (v: string) => void;
  defaultPreset?: Preset;
}

export function AdminDateRangePicker({ startDate, endDate, onStartDateChange, onEndDateChange, defaultPreset = "This Quarter" }: Props) {
  const [preset, setPreset] = useState<Preset>(defaultPreset);
  const [open, setOpen] = useState(false);

  function selectPreset(p: Preset) {
    setPreset(p);
    setOpen(false);
    const { start, end } = computePresetDates(p);
    onStartDateChange(start);
    onEndDateChange(end);
  }

  function handleManualChange(type: "start" | "end", v: string) {
    setPreset("" as Preset);
    if (type === "start") onStartDateChange(v);
    else onEndDateChange(v);
  }

  return (
    // Phones: preset dropdown on its own row above the two dates.
    <div className="flex items-center gap-3 max-md:flex-col-reverse max-md:items-start">
      <div className="flex items-center gap-3 max-md:gap-2">
        <DatePicker value={startDate} onChange={(v) => handleManualChange("start", v)} />
        <Text variant="bodySm" tone="muted">—</Text>
        <DatePicker value={endDate} onChange={(v) => handleManualChange("end", v)} />
      </div>

      <div className="relative ml-auto max-md:ml-0">
        <Button
          variant="ghost"
          size="sm"
          className="!h-auto gap-2 rounded-xl border border-borderDefault bg-bgWhite px-4 py-2.5 shadow-[0px_2px_12px_0px_rgba(190,189,217,0.2)] hover:bg-bgLight"
          onClick={() => setOpen((o) => !o)}
        >
          <IconCalendar size={18} className="text-textMuted" />
          <Text variant="bodySm" tone="dark" inline>{preset || "Custom"}</Text>
          <IconChevronDown size={16} className="text-textDark" />
        </Button>
        {open && (
          <div className="absolute right-0 top-full z-10 mt-1 flex flex-col rounded-xl border border-borderDefault bg-bgWhite py-1 shadow-lg">
            {PRESETS.map((p) => (
              <Button
                key={p}
                variant="ghost"
                size="sm"
                className="!h-auto w-full !justify-start rounded-none px-4 py-2 hover:bg-bgLight"
                onClick={() => selectPreset(p)}
              >
                <Text variant="bodySm" tone={p === preset ? "dark" : "body"} inline>{p}</Text>
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
